import c from 'clsx';

type Pagination = {
    perPage: number;
    total: number;
    currentPage: number;
};

export function getPagination(args: Pagination) {
    const { perPage, total, currentPage } = args;

    return {
        pages: Math.ceil(total / perPage),
        offset: (currentPage - 1) * perPage,
        limit: perPage,
    };
}

export const ELLIPSIS = { key: '…' };

const range = (from: number, to: number) => {
    const r = [];
    for (let i = from; i <= to; i++) r.push(i);
    return r;
};

type WindowItem = number | typeof ELLIPSIS;

export function buildWindow(current: number, total: number, siblings: number): WindowItem[] {
    if (total <= 0 || !Number.isFinite(total)) return [];

    // Few enough pages to show all
    const slots = siblings * 2 + 5; // siblings each side + first + last + current + 2 ellipsis
    if (total <= slots) {
        return range(1, total);
    }

    const leftSiblingEnd = current - 1;
    const rightSiblingStart = current + 1;

    // When to show ellipsis: only if there are 2+ hidden pages (avoids "1 … 3")
    const showLeftEllipsis = leftSiblingEnd - siblings > 2;
    const showRightEllipsis = rightSiblingStart + siblings < total - 1;

    if (!showLeftEllipsis && showRightEllipsis) {
        // Near the start: show first (siblings*2 + 3) pages, …, last
        const leftCount = siblings * 2 + 3;
        return [...range(1, leftCount), ELLIPSIS, total];
    }

    if (showLeftEllipsis && !showRightEllipsis) {
        // Near the end: 1, …, last (siblings*2 + 3) pages
        const rightCount = siblings * 2 + 3;
        return [1, ELLIPSIS, ...range(total - rightCount + 1, total)];
    }

    // Middle: 1, …, current-siblings..current+siblings, …, last
    return [
        1,
        ELLIPSIS,
        ...range(current - siblings, current + siblings),
        ELLIPSIS,
        total,
    ];
}

type PaginationProps = {
    pagesNeighbour: number;
    pages: number;
    currentPage: number;
    buildHref: (page: number) => string;
    goTo: (page: number) => void;
};

export default function Pagination(props: PaginationProps) {
    const w = buildWindow(props.currentPage, props.pages, props.pagesNeighbour);

    return (
        <footer class="episodes__footer">
            {props.currentPage > 1 && (
                <a
                    class="button_pagination"
                    href={props.buildHref(props.currentPage - 1)}
                    onClick={(e) => { e.preventDefault(); props.goTo(props.currentPage - 1); }}
                >
                    <svg width="8" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 0l1.41 1.41L2.83 6l4.58 4.59L6 12 0 6l6-6z" fill="#FFF" />
                    </svg>
                </a>
            )}
            {w.map((item) =>
                typeof item === 'number' ? (
                    <a
                        href={props.buildHref(item)}
                        class={c('pagination__num', {
                            'pagination__active bg-primary': props.currentPage === item,
                        })}
                        onClick={(e) => { e.preventDefault(); props.goTo(item); }}
                    >
                        {item}
                    </a>
                ) : (
                    <span class="pagination__num pagination__disabled">{item.key}</span>
                ),
            )}
            {props.currentPage < props.pages && (
                <a
                    class="button_pagination"
                    href={props.buildHref(props.currentPage + 1)}
                    onClick={(e) => { e.preventDefault(); props.goTo(props.currentPage + 1); }}
                >
                    <svg width="8" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 0L.59 1.41 5.17 6 .59 10.59 2 12l6-6-6-6z" fill="#FFF" />
                    </svg>
                </a>
            )}
        </footer>
    );
}
